import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import PageHeader from "../../components/PageHeader";
import DeleteModal from "../../components/modals/DeleteModal";
import Loading from "../../components/utils/Loading";
import { formattedError } from "../../components/formatted/FormattedText";
import { handleEditFormSubmit } from "../../components/modals/EditableTable";
import { isVerifyPassword } from "./CheckPassword";
import {
  normalizeUserFormForDirtyCheck,
  validateAllUserWizardSteps,
} from "./userAccessCreateWizard";
import {
  UserAccessGeneralSection,
  UserAccessNotificationsSection,
  UserAccessReportsSection,
  UserAccessSitesSection,
} from "./UserAccessFormSections";
import SettingsChangeHistoryPanel from "../settings/SettingsChangeHistoryPanel";
import { syncSessionUserNotificationPrefs } from "../../shared/utils/pushHealth";
import { unlockUser } from "../../services/users";
import { useIsAdminViewMode } from "../../hooks/useViewMode";

const TAB_IDS = {
  general: "general",
  sites: "sites",
  notifications: "notifications",
  reports: "reports",
  modifications: "modifications",
};

const prepareUserFormData = (user) => {
  const {
    authorizedAddressesByCorporation: _authorizedAddressesByCorporation,
    hash: _hash,
    salt: _salt,
    ...safeUser
  } = user || {};
  return {
    ...safeUser,
    role: "owner",
    password: "",
    passwordConfirm: "",
  };
};

const formatUserDisplayName = (user) => {
  if (!user) return "";
  const firstName = user.firstName
    ? user.firstName.charAt(0).toUpperCase() +
      user.firstName.slice(1).toLowerCase()
    : "";
  const lastName = user.lastName ? user.lastName.toUpperCase() : "";
  const fullName = `${lastName} ${firstName}`.trim();
  return fullName || user.email || "";
};

const UserAccessEditEditor = ({ corporation }) => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const API = process.env.REACT_APP_API_BACKEND;
  const token = Cookies.get("userToken");

  const googleSSORequired = !!(corporation && corporation.googleSSORequired);
  const isAdmin = useIsAdminViewMode();

  const [formData, setFormData] = useState(null);
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_IDS.general);
  const [isLoading, setIsLoading] = useState(true);
  const [errorForm, setErrorForm] = useState("");
  const [saving, setSaving] = useState(false);
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const multiselectAddressRef = useRef();

  const tabs = useMemo(
    () => [
      { id: TAB_IDS.general, label: t("settingsUsers.stepProfile") },
      { id: TAB_IDS.sites, label: t("settingsUsers.stepSites") },
      {
        id: TAB_IDS.notifications,
        label: t("settingsUsers.stepNotifications"),
      },
      { id: TAB_IDS.reports, label: t("settingsUsers.stepReports") },
      { id: TAB_IDS.modifications, label: t("settingsHistory.tab") },
    ],
    [t],
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!formData || !initialSnapshot) return false;
    return (
      JSON.stringify(normalizeUserFormForDirtyCheck(formData)) !==
      JSON.stringify(initialSnapshot)
    );
  }, [formData, initialSnapshot]);

  const showFooter = hasUnsavedChanges;
  const contentBottomPadding = showFooter ? "pb-28" : "pb-6";

  useEffect(() => {
    if (!token || !id) return undefined;

    let cancelled = false;

    const loadUser = async () => {
      // Optional fast paint from router state, then always refresh from API
      // so mobile/web edits stay in sync without login/logout.
      const fromState = location.state?.user;
      if (fromState && String(fromState._id) === String(id)) {
        const prepared = prepareUserFormData(fromState);
        setFormData(prepared);
        setInitialSnapshot(normalizeUserFormForDirtyCheck(prepared));
        setIsLoading(false);
      }

      try {
        const response = await axios.get(`${API}access/${id}`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        const user = response.data?.user;
        if (response.status === 200 && user) {
          const prepared = prepareUserFormData({
            ...user,
            diagnosis: response.data?.diagnosis,
            pushAtRisk: response.data?.pushAtRisk,
            fcmTokenCount: Array.isArray(user.fcmTokens)
              ? user.fcmTokens.length
              : user.fcmTokenCount || 0,
          });
          setFormData(prepared);
          setInitialSnapshot(normalizeUserFormForDirtyCheck(prepared));
        } else if (!fromState) {
          history.replace("/access");
        }
      } catch {
        if (!cancelled && !fromState) {
          history.replace("/access");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadUser();
    return () => {
      cancelled = true;
    };
  }, [API, history, id, location.state?.user, token]);

  useEffect(() => {
    if (!token) return;

    const fetchSites = async () => {
      try {
        const res = await axios.get(`${API}address/getAddressNameList`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (res.status === 200) {
          setSites(res.data.addressNameList || []);
        }
      } catch (err) {
        console.error(err);
        setSites([]);
      }
    };

    fetchSites();
  }, [API, token]);

  useEffect(() => {
    if (!formData?.authorizedAddresses?.length || !sites.length) {
      setSelectedSites([]);
      return;
    }

    const preselected = sites.filter((addr) =>
      formData.authorizedAddresses.includes(addr._id),
    );
    setSelectedSites(preselected);
  }, [formData?.authorizedAddresses, sites]);

  const handleAuthorizedAddresses = (addressesToAdd) => {
    setSelectedSites(addressesToAdd);
    setFormData((prev) => ({
      ...(prev || {}),
      authorizedAddresses: addressesToAdd.map((a) => a._id),
    }));
  };

  const saveUser = async () => {
    if (
      !isVerifyPassword(
        googleSSORequired,
        formData.password,
        formData.passwordConfirm,
        setErrorForm,
        t,
        {
          allowEmpty: true,
        },
      )
    ) {
      return false;
    }

    const validation = validateAllUserWizardSteps(formData, t, {
      googleSSORequired,
      isCreate: false,
    });
    if (!validation.valid) {
      setErrorForm(validation.message);
      toast.error(validation.message, { className: "custom-toast" });
      return false;
    }

    setSaving(true);
    setErrorForm("");
    try {
      const {
        authorizedAddressesByCorporation: _authorizedAddressesByCorporation,
        hash: _hash,
        salt: _salt,
        passwordConfirm: _passwordConfirm,
        ...userPayload
      } = formData;
      const response = await handleEditFormSubmit(
        { ...userPayload, role: "owner" },
        "access",
      );
      if (response?.status === 200) {
        const prepared = prepareUserFormData(formData);
        setFormData(prepared);
        setInitialSnapshot(normalizeUserFormForDirtyCheck(prepared));
        const syncedSessionUser = syncSessionUserNotificationPrefs(formData);
        if (syncedSessionUser) {
          // Keep App state aligned when editing the logged-in user.
          window.dispatchEvent(
            new CustomEvent("sessionUserNotificationsUpdated", {
              detail: { user: syncedSessionUser },
            }),
          );
        }
        toast.success(t("The user has been successfully updated"), {
          className: "custom-toast",
        });
        return true;
      }
      const message =
        formattedError(response?.data?.error) || t("An error occurred");
      setErrorForm(message);
      toast.error(message, { className: "custom-toast" });
      return false;
    } catch (error) {
      const message = formattedError(error.response?.data?.error);
      setErrorForm(message);
      toast.error(message, { className: "custom-toast" });
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (!token) return null;
  if (isLoading) return <Loading color="#F5F5F5" />;
  if (!formData) return null;

  const pageTitle = formatUserDisplayName(formData);
  const isLocked = Boolean(formData.isLocked);

  const handleUnlock = async () => {
    if (!formData?._id || unlocking) return;

    setUnlocking(true);
    try {
      await unlockUser(formData._id);
      setFormData((prev) =>
        prev
          ? {
              ...prev,
              isLocked: false,
              failedLoginAttempts: 0,
              lockedUntil: null,
            }
          : prev,
      );
    } catch (error) {
      console.error(error);
      toast.error(t("settingsUsers.unlockError"), {
        className: "custom-toast",
      });
    } finally {
      setUnlocking(false);
    }
  };

  const lockedStatusBadge = isLocked ? (
    <span
      className="inline-flex items-center shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded bg-[#fdecec] text-[#B42318]"
      title={t("settingsUsers.lockedBadgeHint")}
    >
      {t("settingsUsers.lockedBadge")}
    </span>
  ) : null;

  const unlockButton = isLocked ? (
    <button
      type="button"
      className="inline-flex items-center shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded border border-[#B42318] text-[#B42318] bg-white hover:bg-[#fdecec] disabled:opacity-60"
      onClick={handleUnlock}
      disabled={unlocking}
    >
      {unlocking
        ? t("settingsUsers.unlocking")
        : t("settingsUsers.unlockAction")}
    </button>
  ) : null;

  const headerStatusBadges =
    isLocked && (lockedStatusBadge || unlockButton) ? (
      <>
        {lockedStatusBadge}
        {unlockButton}
      </>
    ) : null;

  const tabContent =
    activeTab === TAB_IDS.general ? (
      <UserAccessGeneralSection
        data={formData}
        setData={setFormData}
        isCreate={false}
        googleSSORequired={googleSSORequired}
        setErrorForm={setErrorForm}
        passwordResetKey={id}
      />
    ) : activeTab === TAB_IDS.sites ? (
      <UserAccessSitesSection
        sites={sites}
        selectedSites={selectedSites}
        onAuthorizedAddressesChange={handleAuthorizedAddresses}
        multiselectAddressRef={multiselectAddressRef}
      />
    ) : activeTab === TAB_IDS.notifications ? (
      <UserAccessNotificationsSection
        data={formData}
        setData={setFormData}
        showNotificationTest={true}
        showPushDiagnostics={isAdmin}
        onPushDiagnosticsUpdated={(patch) => {
          setFormData((prev) =>
            prev
              ? {
                  ...prev,
                  pushDiagnostics:
                    patch.pushDiagnostics || prev.pushDiagnostics,
                  diagnosis: patch.diagnosis || prev.diagnosis,
                }
              : prev,
          );
        }}
      />
    ) : activeTab === TAB_IDS.reports ? (
      <UserAccessReportsSection data={formData} setData={setFormData} />
    ) : null;

  const actionsMenuItems = formData?._id
    ? [
        {
          label: t("Delete"),
          icon: <i className="far fa-trash-alt text-[#222222]" aria-hidden />,
          onClick: () => setShowDeleteModal(true),
        },
      ]
    : null;

  return (
    <div className="settings-page-wrapper user-access-editor">
      <PageHeader
        titleContent={
          <div className="flex items-center gap-3 flex-wrap min-w-0">
            <span className="min-w-0">{pageTitle}</span>
            {headerStatusBadges}
          </div>
        }
        titleContentMain={<span>{pageTitle}</span>}
        titleContentBadges={headerStatusBadges}
        showBackButton={true}
        backRoute="/access"
        backLabel={t("settingsUsers.backToList")}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        actionsMenuItems={actionsMenuItems}
      />

      <div
        className={`settings-create-wizard--user bg-[#F5F5F5] min-h-[50vh] ${contentBottomPadding}`}
      >
        {activeTab === TAB_IDS.modifications ? (
          <div className="settings-content-wrapper">
            <SettingsChangeHistoryPanel entityType="user" entityId={id} />
          </div>
        ) : (
          <div className="flex w-full justify-center px-8 py-8">
            <div className="w-full max-w-[640px]">
              {errorForm ? (
                <section className="user-access-form-error mb-4">
                  <p>{errorForm}</p>
                </section>
              ) : null}
              {tabContent}
            </div>
          </div>
        )}
      </div>

      {showFooter ? (
        <div className="settings-wizard-footer">
          <div
            className="settings-wizard-footer__inner"
            style={{ maxWidth: 640 }}
          >
            <button
              type="button"
              className="modal-button modal-button-cancel"
              onClick={() => history.push("/access")}
              disabled={saving}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="modal-button modal-button-save"
              onClick={saveUser}
              disabled={saving}
            >
              {t("Save")}
            </button>
          </div>
        </div>
      ) : null}

      {showDeleteModal && formData?._id ? (
        <DeleteModal
          modal={showDeleteModal}
          setModal={setShowDeleteModal}
          id={formData._id}
          message1={`${t("Do you really want to delete the user")} : \n${formData.firstName} ?`}
          message2={formData.email}
          messageValidation={t("The user has been successfully deleted")}
          path="user/delete/"
          tableData={[]}
          setTableData={() => {}}
          setIsDeleted={() => history.push("/access")}
        />
      ) : null}
    </div>
  );
};

export default UserAccessEditEditor;
